import React, { useContext, useEffect, useState } from 'react'
import { uniqBy } from 'lodash'

import MainModal from './common/MainModal'
import { AddTeamMemberToPaperworkHeader } from './components/AddTeamMemberToPaperworkHeader'
import { SearchUsers } from './components/SearchUsers'
import { IconPlus } from '../icons/IconPlus'

import useToasterHelper from '../../helpers/ToasterHelper'

import UserService from '../../services/firebase-service/users/userService'
import { HandleModalContext } from '../../layout/mainlayout/MainLayout'

import { IUserInterface } from '../../models/users/user.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IRiderTeamMemberInterface } from '../../models/rider-team-member/riderTeamMember.interface'

import { MODAL_CONSTS } from '../../const/modal-const'

interface IUserExtendedWithSelected extends IUserInterface {
  selected?: boolean
  status?: string
}

type IProps = {
  dataToPassOn: {
    riderTeamMembers: IRiderTeamMemberInterface[]
    registrationByDay: IRegistrationByDayInterface
    defaultRole?: string
    defaultTeamMemberId?: string
    onlyPaperwork?: boolean
  }
}

export const AddTeamMemberToPaperworkStep1: React.FC<IProps> = ({ dataToPassOn }) => {
  const toastFunction = useToasterHelper()
  const handleModalContext = useContext(HandleModalContext)

  const { onlyPaperwork, registrationByDay, riderTeamMembers, defaultRole, defaultTeamMemberId } =
    dataToPassOn

  const [listToShow, setListToShow] = useState<IUserExtendedWithSelected[]>([])
  const [defaultUsers, setDefaultUsers] = useState<IUserInterface[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)

  useEffect(() => {
    const getAllCompetitors = async () => {
      try {
        setSearchLoading(true)
        const userIds: Set<string> = new Set()
        riderTeamMembers.forEach((item) => {
          if (item.riderId) userIds.add(item.riderId)
          if (item.teamMemberId) userIds.add(item.teamMemberId)
        })
        const usersInDb = await UserService.getByIds([...userIds])

        let selectedUsers = usersInDb.map((user) => {
          if (defaultTeamMemberId === user.id) return { ...user, selected: true }
          else return user
        })

        if (onlyPaperwork) selectedUsers = selectedUsers.filter((item) => item.selected)

        setDefaultUsers(usersInDb)
        setListToShow(selectedUsers)
      } catch (error) {
        console.error('error', error)
      } finally {
        setSearchLoading(false)
      }
    }
    getAllCompetitors().then()
  }, [defaultTeamMemberId, onlyPaperwork, riderTeamMembers])

  const handleUsers = async (strToSearch: string) => {
    setSearchValue(strToSearch)

    try {
      setSearchLoading(true)
      const search = strToSearch.toLowerCase().replace(/\s+/g, '')
      const currentSelected = listToShow?.filter((item) => item.selected)
      if (search.length < 3) {
        setListToShow(uniqBy([...currentSelected, ...defaultUsers], 'id'))
        return
      }
      const usersInDb = await UserService.getByName(search)
      setListToShow(uniqBy([...currentSelected, ...usersInDb], 'id'))
    } catch (error) {
      console.error('error: ', error)
    } finally {
      setSearchLoading(false)
    }
  }

  const handleChange = (team_member: IUserExtendedWithSelected) => {
    if (listToShow) {
      const sameTypeUsersArrCopy = listToShow.map((currUser) => {
        if (currUser.id === team_member?.id) {
          return { ...currUser, selected: !currUser.selected }
        } else {
          return currUser
        }
      })
      setListToShow(sameTypeUsersArrCopy)
    }
  }

  return (
    <MainModal
      title={onlyPaperwork ? 'Add paperwork' : 'Add team member to entry'}
      show={true}
      type="ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_1"
      size="xl"
      titleClassName="!font-normal"
      className="z-[50]"
      overlayClassName="z-[49]"
      buttons={[
        {
          label: 'Next >',
          bgClass: 'bg-SeabiscuitMainThemeColor',
          fullWidth: true,
          onClick: () => {
            if (!Array.isArray(listToShow)) return null
            const teamMembersFiltered = listToShow.filter((itm) => itm.selected)

            if (!teamMembersFiltered.length)
              return toastFunction.info({ message: 'At least one user should be selected' })

            handleModalContext?.handleModal(false, MODAL_CONSTS.ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_1)
            handleModalContext?.handleModal(
              true,
              MODAL_CONSTS.ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_2,
              {
                ...handleModalContext?.dataToPassOn,
                rider: registrationByDay,
                teamMembers: teamMembersFiltered,
                defaultRole: defaultRole,
                onlyPaperwork: onlyPaperwork,
              }
            )
          },
          className: 'outline-none w-full',
          textClass: 'text-white',
          disabled: !listToShow?.length || searchLoading,
        },
        {
          label: 'CANCEL',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          className: 'outline-none',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          fullWidth: true,
          disabled: searchLoading,
          onClick: () =>
            handleModalContext?.handleModal(
              false,
              MODAL_CONSTS.ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_1
            ),
        },
      ]}
    >
      <AddTeamMemberToPaperworkHeader registrationByDay={registrationByDay} />
      {!onlyPaperwork && (
        <div className="inputSearch mt-4 flex items-center relative">
          <input
            type="text"
            onChange={(e) => handleUsers(e.target.value)}
            placeholder="Enter at least 3 letters..."
            className="rounded-full w-full border focus:border-[#D3DAEE] border-[#D3DAEE] text-SeabiscuitDark200ThemeColor focus:ring-0"
          />

          <div className="absolute right-0 top-0 h-full w-[135px] flex justify-center items-center">
            <span className="absolute left-0 h-[60%] top-[50%] translate-y-[-50%] w-[1px] bg-[#122B4680]" />
            <button
              onClick={() => {
                handleModalContext?.handleModal(
                  false,
                  MODAL_CONSTS.ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_1
                )
                handleModalContext?.handleModal(
                  true,
                  MODAL_CONSTS.CREATE_NEW_TEAM_MEMBER_PAPERWORK,
                  {
                    ...handleModalContext?.dataToPassOn,
                    rider: registrationByDay,
                  }
                )
              }}
              className={
                'group text-[#122B4680] text-[14px] flex items-center gap-1 hover:text-SeabiscuitMainThemeColor'
              }
            >
              <IconPlus /> New Person
            </button>
          </div>
        </div>
      )}
      <SearchUsers
        loading={searchLoading}
        users={listToShow}
        searchValue={searchValue}
        onToggleSelect={handleChange}
      />
    </MainModal>
  )
}
